import React, { useState, useEffect } from "react";
import { CgMenuRightAlt } from "react-icons/cg";
import { AiOutlineClose } from "react-icons/ai";

const Navbar = () => {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isSticky, setIsSticky] = useState(false);
  const [isScreenNarrow, setIsScreenNarrow] = useState(false);

  const menuItems = {
    about: {
      label: "HOME",
      items: [
        { path: "/", label: "ABOUT US" },
        { path: "/#vision", label: "VISION" },
        { path: "/#team", label: "TEAM" },
        { path: "/#our-story", label: "OUR STORY" },
        { path: "/#contact", label: "CONTACT" },
        { path: "/#what-we-do", label: "WHAT WE DO" }
      ]
    },
    volunteer: {
      label: "VOLUNTEER",
      items: [
        { path: "/volunteer/calendar", label: "CALENDAR" },
        { path: "/volunteer/branch-creation", label: "BRANCH CREATION" },
        { path: "/volunteer/branch-renewal", label: "BRANCH RENEWAL" }
      ]
    },
    publications: {
      label: "PUBLICATIONS",
      items: [
        { path: "/publications/sfr-reflections", label: "SFR REFLECTIONS" },
      ]
    },
    gallery: {
      label: "GALLERY",
      items: [
        { path: "/gallery/events", label: "EVENT GALLERY" },
        { path: "/gallery/newsletters", label: "NEWSLETTERS GALLERY" }
      ]
    },
    // resources: {
    //   label: "RESOURCES",
    //   items: [
    //     { path: "/resources/general", label: "GENERAL WEBSITES" },
    //     { path: "/resources/africa", label: "AFRICAN REFUGEE CRISIS" },
    //     {
    //       path: "/resources/middleeast",
    //       label: "MIDDLE EASTERN REFUGEE CRISIS"
    //     },
    //     { path: "/resources/policy", label: "REFUGEE MIGRATION POLICY" },
    //     {
    //       path: "/resources/latinamerica",
    //       label: "LATIN AMERICA REFUGEE CRISIS"
    //     }
    //   ]
    // }
  };

  const standardMenuItems = [
    // { path: "/", label: "HOME" },
    { path: "/impact", label: "IMPACT" },
    { path: "/merch", label: "MERCH"}
  ];

  // Add effect to handle scroll and resize events
  useEffect(() => {
    const handleScroll = () => {
      // Only make navbar sticky when scrolled down past header height and screen is narrow
      if (isScreenNarrow) {
        setIsSticky(window.scrollY > 50);
      } else {
        setIsSticky(false);
      }
    };

    const handleResize = () => {
      // Match the same breakpoint used in FloatingBottomNav (640px)
      setIsScreenNarrow(window.innerWidth < 640);
      handleScroll(); // Update sticky state based on new screen size
    };

    // Initial setup
    handleResize();

    // Add event listeners
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    
    // Clean up
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [isScreenNarrow]);

  // If not on homepage, let default navigation happen (browser will navigate to /)
  // Then use onload or useEffect to scroll to the section after page load
  const handleLinkClick = (e, path) => {
    // Special case for home button
    if (path === "/") {
      const isHomepage = window.location.pathname === "/" || window.location.pathname === "";
      if (isHomepage) {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      // Otherwise let default navigation happen
      return;
    }
    
    // Existing code for section navigation
    if (path.includes("/#")) {
      const isHomepage = window.location.pathname === "/" || window.location.pathname === "";
      
      if (isHomepage) {
        e.preventDefault();
        const sectionId = path.split("#")[1];
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
          setActiveDropdown(null);
        }
      } else {
        const sectionId = path.split("#")[1];
        sessionStorage.setItem("scrollToSection", sectionId);
      }
    }
  };

  // Add this useEffect in your Homepage component:
  useEffect(() => {
    // Check if we need to scroll to a section
    const scrollToSection = sessionStorage.getItem("scrollToSection");
    if (scrollToSection) {
      // Clear it immediately to prevent future unwanted scrolls
      sessionStorage.removeItem("scrollToSection");

      // Slight delay to ensure all content is rendered
      setTimeout(() => {
        const element = document.getElementById(scrollToSection);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  }, []);

  const NavItem = ({ children, className = "", isMobile = false }) => (
    <div className={`group relative ${isMobile ? "w-full" : ""}`}>
      <div className={`relative ${className}`}>
        {children}
        <div
          className={`absolute bottom-0 left-0 h-0.5 w-0 bg-primary transition-all duration-300 group-hover:w-full ${
            isMobile ? "bottom-0" : ""
          }`}
        ></div>
        <div
          className={`absolute bottom-0 left-0 h-0 w-0.5 bg-primary transition-all duration-300 group-hover:h-1/2 ${
            isMobile ? "h-full group-hover:h-full" : ""
          }`}
        ></div>
      </div>
    </div>
  );

  const getDropdownAlignment = (key) => {
    if (key === "resources" || key === "gallery") {
      return "right-0";
    }
    return "left-0";
  };

  const DropdownMenu = ({ items, type }) => (
    <ul
      className={`absolute ${getDropdownAlignment(
        type
      )} top-full z-50 w-80 bg-background shadow-lg rounded-b-lg`}
      style={{ maxHeight: "80vh", overflowY: "auto" }}
    >
      {items.map((item) => (
        <li key={item.path} className="w-full">
          <NavItem className="w-full">
            <a
              href={item.path}
              className="block w-full px-4 py-2 text-sm text-secondary text-left transition-colors hover:bg-lightTeal"
              onClick={(e) => handleLinkClick(e, item.path)}
            >
              {item.label}
            </a>
          </NavItem>
        </li>
      ))}
    </ul>
  );

  const MobileMenuItem = ({ item, onClick }) => (
    <NavItem isMobile className="w-full mb-2">
      <a
        href={item.path || "#"}
        className="block w-full px-4 py-2 text-lg text-secondary hover:bg-lightTeal transition-colors"
        onClick={(e) => {
          handleLinkClick(e, item.path);
          if (onClick) onClick();
        }}
      >
        {item.label}
      </a>
    </NavItem>
  );

  const MobileSubmenu = ({ items, onBack }) => (
    <div className="fixed right-0 top-0 h-full w-80 bg-background shadow-lg transform transition-all duration-300 ease-in-out z-50">
      <div className="flex justify-between items-center p-4 border-b border-lightTeal">
        <button
          onClick={onBack}
          className="text-lg flex items-center text-secondary"
        >
          <span className="mr-2">«</span> Back
        </button>
        <button onClick={() => setIsNavOpen(false)} className="p-2">
          <AiOutlineClose size={20} />
        </button>
      </div>
      <div className="p-4">
        {items.map((item) => (
          <MobileMenuItem
            key={item.path}
            item={item}
            onClick={() => {
              setIsNavOpen(false);
              setActiveSubmenu(null);
            }}
          />
        ))}
      </div>
    </div>
  );

  return (
    <nav className={`right-0 z-50 bg-white ${isSticky ? 'fixed top-0 left-0 w-full shadow-md' : ''}`}>
      <div className="w-full bg-transparent px-4 md:px-6 border-b border-lightTeal">
        <div className="mx-auto flex h-20 w-full items-center justify-between md:h-28">
          <a href="/" className="flex-shrink-0">
            <div className="whitespace-nowrap font-display text-h3 font-bold text-primary">
              Students for Refugees
            </div>
          </a>

          <ul className="hidden items-center gap-6 lg:flex">
            {standardMenuItems.map((item) => (
              <li key={item.path}>
                <NavItem>
                  <a
                    href={item.path}
                    className="px-2 font-body text-nav text-secondary"
                    onClick={(e) => handleLinkClick(e, item.path)}
                  >
                    {item.label}
                  </a>
                </NavItem>
              </li>
            ))}

            {Object.entries(menuItems).map(([key, { label, items }]) => (
              <li
                key={key}
                className="relative"
                onMouseEnter={() => setActiveDropdown(key)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <NavItem>
                  <span className="cursor-pointer px-2 font-body text-nav text-secondary">
                    {label}
                  </span>
                </NavItem>
                {activeDropdown === key && (
                  <DropdownMenu items={items} type={key} />
                )}
              </li>
            ))}

            <li>
              <a
                href="/donate"
                // href="/#donation"
                className="rounded-md bg-cta px-4 py-2 font-body text-nav text-background transition-colors hover:bg-opacity-90"
                onClick={(e) => handleLinkClick(e, "/donate")}
                // onClick={(e) => handleLinkClick(e, "/#donation")}
              >
                DONATE
              </a>
            </li>
          </ul>

          <button
            onClick={() => setIsNavOpen(!isNavOpen)}
            className="lg:hidden text-secondary"
          >
            <CgMenuRightAlt size={24} />
          </button>

          {isNavOpen && (
            <div className="fixed inset-0 z-50 lg:hidden">
              <div
                className="absolute inset-0 bg-black bg-opacity-50"
                onClick={() => setIsNavOpen(false)}
              />
              <div className="absolute right-0 top-0 h-full w-80 bg-background shadow-lg overflow-y-auto">
                <div className="flex justify-end border-b border-lightTeal p-4">
                  <button
                    onClick={() => setIsNavOpen(false)}
                    className="p-2 text-secondary"
                  >
                    <AiOutlineClose size={20} />
                  </button>
                </div>
                <div className="p-4">
                  {/* Standard Menu Items */}
                  {standardMenuItems.map((item) => (
                    <MobileMenuItem
                      key={item.path}
                      item={item}
                      onClick={() => setIsNavOpen(false)}
                    />
                  ))}

                  {/* Dropdown Menu Items */}
                  {Object.entries(menuItems).map(([key, { label, items }]) => (
                    <NavItem key={key} isMobile className="w-full mb-2">
                      <button
                        onClick={() => setActiveSubmenu(key)}
                        className="w-full px-4 py-2 text-lg text-secondary text-left hover:bg-lightTeal transition-colors flex justify-between items-center"
                      >
                        {label}
                        <span className="ml-2">»</span>
                      </button>
                    </NavItem>
                  ))}

                  <a
                    href="/#donation"
                    className="mt-4 block rounded-md bg-cta py-2 text-center text-lg text-background transition-colors hover:bg-opacity-90"
                    onClick={(e) => {
                      handleLinkClick(e, "/#donation");
                      setIsNavOpen(false);
                    }}
                  >
                    DONATE
                  </a>
                </div>
              </div>

              {/* Mobile Submenu */}
              {activeSubmenu && (
                <MobileSubmenu
                  items={menuItems[activeSubmenu].items}
                  onBack={() => setActiveSubmenu(null)}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;